import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { tokensFor } from "./tokens";

export type ThemeMode = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "daynav.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

// Phase 6 lime brand. Shared by chat bubble accents, top-nav active item,
// itinerary selection edges, scope chip, must-visit badge, etc. Mirrors
// `--brand` in styles/tokens.css and the `brand` field in ./tokens.ts.
export const BRAND_GREEN = "#B1D137";

type ThemeContextValue = {
  mode: ThemeMode;
  effective: EffectiveTheme;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isHydratingPrerender(): boolean {
  if (typeof window === "undefined") return false;
  return document.getElementById("root")?.dataset.prerendered === "true";
}

function readInitialMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.(DARK_QUERY).matches ?? false;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const hydrating = useRef(isHydratingPrerender());

  const [mode, setModeState] = useState<ThemeMode>(() =>
    hydrating.current ? "system" : readInitialMode(),
  );
  const [systemDark, setSystemDark] = useState<boolean>(() =>
    hydrating.current ? false : systemPrefersDark(),
  );

  useEffect(() => {
    if (hydrating.current) {
      hydrating.current = false;
      setModeState(readInitialMode());
      setSystemDark(systemPrefersDark());
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia?.(DARK_QUERY);
    if (!mql) return;
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    setSystemDark(mql.matches);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  const effective: EffectiveTheme = useMemo(() => {
    if (mode === "system") return systemDark ? "dark" : "light";
    return mode;
  }, [mode, systemDark]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = document.documentElement;
    root.dataset.theme = effective;
    root.style.colorScheme = effective;
  }, [effective]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(effective === "light" ? "dark" : "light");
  }, [effective, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, effective, setMode, toggle }),
    [mode, effective, setMode, toggle]
  );

  const isDark = effective === "dark";
  const t = tokensFor(effective);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: isDark
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
          token: {
            colorPrimary: t.brand,
            colorInfo: t.brand,
            colorSuccess: t.brand,
            colorWarning: "#f59e0b",
            colorError: "#ef4444",
            colorLink: t.ink,
            colorBgContainer: t.surface,
            colorBgElevated: t.surface,
            colorBgLayout: t.bg,
            colorBorder: t.rule,
            colorBorderSecondary: t.rule,
            colorText: t.ink,
            colorTextSecondary: t.ink2,
            colorTextTertiary: t.ink3,
            colorTextQuaternary: t.inkSoft,
            borderRadius: 6,
            borderRadiusLG: 10,
            borderRadiusSM: 6,
            borderRadiusXS: 4,
            wireframe: false,
            fontFamily:
              '"IBM Plex Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          },
          components: {
            Layout: {
              headerBg: t.brand,
              headerHeight: 48,
              bodyBg: t.bg,
            },
            Button: {
              controlHeight: 34,
            },
            Card: {
              borderRadiusLG: 10,
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      mode: "system" as ThemeMode,
      effective: "light" as EffectiveTheme,
      setMode: () => {},
      toggle: () => {},
    };
  }
  return ctx;
}
